/**
 * Where a sign-in bounce sends the visitor back to.
 *
 * One rule, three call sites — ProtectedRoute writes the target, PublicRoute
 * and the sign-in page each read it — so it lives here rather than being
 * restated at each. Two of those restatements had already drifted: both tested
 * `!next.startsWith('//')` and neither knew about the backslash spelling below.
 */

/** Where a visitor goes when no usable target was carried. */
export const DEFAULT_TARGET = '/dashboard';

/**
 * The `?next=` value to honour, or null.
 *
 * Only a same-site absolute path qualifies. The two spellings that matter both
 * begin with `/` and both reach another host: `//evil.example` is a
 * protocol-relative URL, and `/\evil.example` becomes one once the browser
 * normalises the backslash — so a bare `startsWith('/')` check reads as a guard
 * while permitting an open redirect.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

/** `safeNext` with the fallback applied, for callers that must land somewhere. */
export function nextOrDefault(raw: string | null | undefined): string {
  return safeNext(raw) ?? DEFAULT_TARGET;
}

/**
 * The sign-in URL that returns here afterwards.
 *
 * A guard that redirects to a bare `/sign-in` throws the destination away, and
 * the visitor finishes signing in on the dashboard wondering where the page
 * they clicked towards went. That is what happened to every deep link into the
 * dashboard — /dashboard/billing above all, which is where the app's "Top up"
 * button points.
 */
export function signInHref(pathname: string, search = ''): string {
  const target = safeNext(`${pathname}${search}`);
  if (!target || target === DEFAULT_TARGET) return '/sign-in';
  return `/sign-in?next=${encodeURIComponent(target)}`;
}
